// shop-frontend/src/pages/ShopFeed.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CornerUpLeft, Trash2, X, MoreVertical, Share2, Pencil, Image as ImageIcon, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MentionText from "../components/MentionText";
import ShopAddStory from "../components/ShopAddStory";
import {
  addShopComment,
  addShopReply,
  createShopPost,
  deleteShopPost,
  editShopPost,
  getShopMe,
  getShopFeed,
  getMyShopProducts,
  toggleShopLike,
  toggleCommentLike,
  editComment,
  deleteComment,
  request, // ✅ uses shop token
  toAbsUrl, // ✅ centralized image URL helper
} from "../utils/api";
import { useShopAuth } from "../context/ShopAuthContext";
import "../styles/shopFeed.css";

const BACKEND =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  "https://moondala-one.onrender.com";

const USER_APP =
  process.env.REACT_APP_USER_APP_URL ||
  process.env.REACT_APP_USER_FRONTEND_URL ||
  (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? "http://localhost:5173"
    : "https://moondala.one");

function userBuyUrl(productId) {
  return `${USER_APP}/mall/product/${encodeURIComponent(String(productId || ""))}`;
}

function isVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  const u = url.toLowerCase();
  return u.endsWith(".mp4") || u.endsWith(".mov") || u.endsWith(".webm") || u.includes("video");
}

function withCacheBust(url, key = "") {
  const clean = String(url || "").trim();
  if (!clean) return "";
  const sep = clean.includes("?") ? "&" : "?";
  return `${clean}${sep}v=${encodeURIComponent(String(key || Date.now()))}`;
}

// ✅ Use centralized toAbsUrl instead of makeAbsolute
const makeAbsolute = toAbsUrl;

/* =========================================
   ✅ Clean ids (fix ":id" + weird shapes)
   ========================================= */
function cleanIdForPath(v) {
  if (v == null) return "";

  let s = "";
  if (typeof v === "string") s = v;
  else if (typeof v === "object") s = String(v._id || v.id || v.postId || v.entityId || "");
  else s = String(v);

  s = String(s || "").trim();

  if (s.startsWith(":")) s = s.slice(1);
  if (s.includes("/")) s = s.split("/").pop();
  if (s.includes("?")) s = s.split("?")[0];
  if (s.includes("#")) s = s.split("#")[0];

  s = s.replace(/[<>]/g, "").trim();

  if (!s || s === "undefined" || s === "null") return "";
  return s;
}

/* =========================
   POST NORMALIZATION
   ========================= */
function getPostIdFromAnyShape(x) {
  if (!x) return "";

  if (x.type === "post" && x.post) {
    return cleanIdForPath(x.post._id || x.post.id || x.post.postId || "");
  }

  if (x.post) {
    return cleanIdForPath(x.post._id || x.post.id || x.post.postId || "");
  }

  return cleanIdForPath(x._id || x.id || x.postId || "");
}

function extractPostObject(x) {
  if (!x) return null;

  // ✅ Skip product items from combined feed — products are loaded separately
  if (x.type === "product") return null;

  if (x.type === "post" && x.post) return x.post;
  if (x.post && (x.post._id || x.post.id)) return x.post;
  if (x._id || x.id) return x;

  return null;
}

function normalizePostText(post) {
  return post?.text || post?.caption || post?.content || post?.message || "";
}

function normalizePostMediaUrl(post) {
  if (!post) return "";

  if (post._mediaUrl) return makeAbsolute(post._mediaUrl);
  if (post.mediaUrl) return makeAbsolute(post.mediaUrl);

  if (Array.isArray(post.media) && post.media.length) {
    const first = post.media[0];
    if (typeof first === "string") return makeAbsolute(first);
    if (first?.url) return makeAbsolute(first.url);
    if (first?.secure_url) return makeAbsolute(first.secure_url);
    if (first?.src) return makeAbsolute(first.src);
    if (first?.path) return makeAbsolute(first.path);
    if (first?.name) return makeAbsolute(`/uploads/shop-posts/${first.name}`);
    if (first?.key) return makeAbsolute(first.key);
    if (first?.filename) return makeAbsolute(`/uploads/shop-posts/${first.filename}`);
  }

  if (post.media && typeof post.media === "object" && !Array.isArray(post.media)) {
    if (post.media.url) return makeAbsolute(post.media.url);
    if (post.media.secure_url) return makeAbsolute(post.media.secure_url);
    if (post.media.src) return makeAbsolute(post.media.src);
    if (post.media.path) return makeAbsolute(post.media.path);
    if (post.media.key) return makeAbsolute(post.media.key);
    if (post.media.name) return makeAbsolute(`/uploads/shop-posts/${post.media.name}`);
    if (post.media.filename) return makeAbsolute(`/uploads/shop-posts/${post.media.filename}`);
  }

  if (typeof post.media === "string") return makeAbsolute(post.media);

  if (Array.isArray(post.mediaUrls) && post.mediaUrls.length) return makeAbsolute(post.mediaUrls[0]);
  if (Array.isArray(post.images) && post.images.length) return makeAbsolute(post.images[0]);
  if (Array.isArray(post.videos) && post.videos.length) return makeAbsolute(post.videos[0]);

  if (post.imageUrl) return makeAbsolute(post.imageUrl);
  if (post.videoUrl) return makeAbsolute(post.videoUrl);
  if (post.mediaPath) return makeAbsolute(post.mediaPath);
  if (post.fileUrl) return makeAbsolute(post.fileUrl);
  if (post.file) return makeAbsolute(post.file);
  if (post.mediaFilename) {
    const mf = String(post.mediaFilename || "").trim();
    if (!mf) return "";
    if (mf.startsWith("http://") || mf.startsWith("https://") || mf.includes("cloudinary.com") || mf.startsWith("moondala/")) {
      return makeAbsolute(mf);
    }
    return `${BACKEND}/uploads/shop-posts/${mf}`;
  }

  return "";
}

function normalizePostsFromResponse(postsData) {
  const list = postsData?.posts || postsData?.items || postsData?.data || postsData;
  const arr = Array.isArray(list) ? list : [];

  const posts = arr
    .map((raw) => {
      const postObj = extractPostObject(raw);
      if (!postObj) return null;

      // Normalize author/shop object so components can read `author.shopName` and `author._id`
      let author = postObj.author || postObj.shop || postObj.createdBy || postObj.user || postObj.owner || postObj.shop || {};
      if (typeof author === 'string' || typeof author === 'number') {
        author = { _id: String(author) };
      } else if (author && typeof author === 'object') {
        if (!author._id && author.id) author._id = author.id;
      }
      // attach normalized author back to postObj so UI can rely on it
      postObj.author = author;

      const realId =
        getPostIdFromAnyShape(raw) ||
        cleanIdForPath(postObj._id || postObj.id || postObj.postId || "");

      const text = normalizePostText(postObj);
      const media = normalizePostMediaUrl(postObj);

      return {
        ...postObj,
        __id: realId,
        __text: String(text || ""),
        __mediaUrl: String(media || ""),
        createdAt:
          postObj.createdAt ||
          postObj.updatedAt ||
          postObj.time ||
          postObj.date ||
          (raw && raw.createdAt) ||
          (raw && raw.updatedAt) ||
          new Date().toISOString(),
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return posts;
}

/* =========================
   PRODUCT NORMALIZATION
   ========================= */
function getRealProductId(anyShape) {
  if (!anyShape) return null;
  if (anyShape._id) return anyShape._id;
  if (anyShape.id) return anyShape.id;
  if (anyShape.productId) return anyShape.productId;
  if (anyShape.product?._id) return anyShape.product._id;
  if (anyShape.product?.id) return anyShape.product.id;
  return null;
}

// ✅ Prefer variant images first (when available), then fall back to root images.
function normalizeProductImage(prod) {
  if (!prod) return "";

  if (Array.isArray(prod.variants) && prod.variants.length) {
    for (const v of prod.variants) {
      if (Array.isArray(v.images) && v.images.length) {
        const first = String(v.images[0] || "").trim();
        if (first) return makeAbsolute(first);
      }
    }
  }

  if (Array.isArray(prod.images) && prod.images.length) {
    const first = String(prod.images[0] || "").trim();
    if (first) return makeAbsolute(first);
  }

  if (Array.isArray(prod.displayImages) && prod.displayImages.length) {
    const first = String(prod.displayImages[0] || "").trim();
    if (first) return makeAbsolute(first);
  }

  if (Array.isArray(prod.media?.images) && prod.media.images.length) {
    const first = String(prod.media.images[0] || "").trim();
    if (first) return makeAbsolute(first);
  }

  const fallbacks = [
    prod.media?.image,
    prod.imageUrl,
    prod.image,
    prod.mainImage,
    prod.photo,
    prod.thumbnail,
    prod.cover,
    prod.productImage,
    prod.photoUrl,
    prod.thumbnailUrl,
    prod.coverUrl,
  ];

  for (const f of fallbacks) {
    const s = String(f || "").trim();
    if (s) return makeAbsolute(s);
  }

  return "";
}

/**
 * Expand a flat product list to one card per unique colour variant.
 * Falls back to the parent card if the product has no variants/colour option.
 * Each expanded card carries _parentId (the real mongo _id) so toggleFeatured
 * still stores the parent productId in featuredIds.
 */
function expandProductsForFeaturedModal(products) {
  const out = [];
  for (const product of (products || [])) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) { out.push(product); continue; }

    // Detect colour option name
    const optionNames = (product.options || []).map(o => String(o.name || '')).filter(Boolean);
    const colorOptionName = optionNames.find(n => /colou?r/i.test(n)) || optionNames[0] || null;

    if (!colorOptionName) { out.push(product); continue; }

    // One card per unique colour (first active variant per colour wins)
    const seen = new Set();
    for (const v of variants) {
      if (v.isActive === false) continue;
      const colorVal = ((v.optionValues || []).find(ov => ov.name === colorOptionName)?.value || '').trim();
      if (!colorVal || seen.has(colorVal.toLowerCase())) continue;
      seen.add(colorVal.toLowerCase());

      const variantImg = Array.isArray(v.images) && v.images[0]
        ? makeAbsolute(String(v.images[0]))
        : normalizeProductImage(product);

      out.push({
        ...product,
        _parentId: String(product._id || product.id || ''), // real id for featuredIds
        _image: variantImg,
        _variantLabel: colorVal,
        _variantId: String(v._id || ''),
        title: `${product.title || product.name || 'Product'} – ${colorVal}`,
      });
    }
    // safety: if all variants were inactive, push the parent
    if (seen.size === 0) out.push(product);
  }
  return out;
}

/* =========================================
   ✅ FEATURED PRODUCTS (BACKEND PERSISTENCE)
   ========================================= */
function pickFeaturedIdsFromAnyResponse(res) {
  const ids =
    res?.featuredProductIds ||
    res?.featuredIds ||
    res?.ids ||
    res?.data?.featuredProductIds ||
    res?.data?.ids ||
    null;

  if (Array.isArray(ids) && ids.length) return ids.map(String).filter(Boolean);

  const prods =
    (Array.isArray(res?.products) && res.products) ||
    (Array.isArray(res?.data?.products) && res.data.products) ||
    null;

  if (Array.isArray(prods) && prods.length) {
    return prods.map((p) => String(getRealProductId(p) || "")).filter(Boolean);
  }

  return [];
}

async function loadFeaturedIdsFromBackendSafe(setDebug) {
  const tries = ["/shop/featured-products", "/shops/featured-products"];

  let lastErr = null;
  for (const path of tries) {
    try {
      const res = await request(path, { method: "GET" });
      const outIds = pickFeaturedIdsFromAnyResponse(res);
      const outVariantIds = Array.isArray(res?.featuredVariantIds) ? res.featuredVariantIds.map(String).filter(Boolean) : [];
      const outConfig = res?.marqueeConfig || { enabled: true, speed: 26, pauseOnHover: true };
      
      return { featuredIds: outIds, featuredVariantIds: outVariantIds, marqueeConfig: outConfig };
    } catch (e) {
      lastErr = e;
      if (setDebug) setDebug(`Featured GET failed: ${path} (${e?.status || "?"}) ${e?.message || ""}`);
    }
  }

  if (lastErr) console.warn("Featured GET failed:", lastErr?.message || lastErr);
  return { featuredIds: [], featuredVariantIds: [], marqueeConfig: { enabled: true, speed: 26, pauseOnHover: true } };
}

async function saveFeaturedIdsToBackendSafe(ids, variantIds, marqueeConfig, setDebug) {
  const payload = { 
    featuredProductIds: (ids || []).map(String),
    featuredVariantIds: (variantIds || []).map(String),
    marqueeConfig: marqueeConfig || { enabled: true, speed: 26, pauseOnHover: true }
  };
  const tries = ["/shop/featured-products", "/shops/featured-products"];

  let lastErr = null;
  for (const path of tries) {
    try {
      const res = await request(path, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const out =
        res?.featuredProductIds ||
        res?.featuredIds ||
        res?.ids ||
        res?.data?.featuredProductIds ||
        payload.featuredProductIds;

      const outIds = Array.isArray(out) ? out.map(String).filter(Boolean) : payload.featuredProductIds;
      const outVariantIds = Array.isArray(res?.featuredVariantIds) ? res.featuredVariantIds.map(String).filter(Boolean) : payload.featuredVariantIds;
      const outConfig = res?.marqueeConfig || payload.marqueeConfig;

      if (setDebug) setDebug(`Featured PUT OK: ${path} (count=${outIds.length} variants=${outVariantIds.length})`);
      return { featuredIds: outIds, featuredVariantIds: outVariantIds, marqueeConfig: outConfig };
    } catch (e) {
      lastErr = e;
      if (setDebug) setDebug(`Featured PUT failed: ${path} (${e?.status || "?"}) ${e?.message || ""}`);
    }
  }

  if (lastErr) console.warn("Featured PUT failed:", lastErr?.message || lastErr);
  throw lastErr || new Error("Failed to save featured products");
}

/* =========================================
   Featured Products Row (moves left)
   ========================================= */
function ProductsRowMarquee({ products, onOpenDetails, onOpenUser, onOpenManage, marqueeConfig }) {
  if (!products?.length) return null;

  const max = Math.min(products.length, 14);
  const list = products.slice(0, max);
  const display = [...list, ...list];
  
  // Apply marquee configuration
  const isEnabled = marqueeConfig?.enabled !== false;
  const speed = marqueeConfig?.speed || 26;
  const pauseOnHover = marqueeConfig?.pauseOnHover !== false;
  
  // Dynamic styles for configurable animation
  const trackStyle = isEnabled ? {
    '--marquee-duration': `${speed}s`,
    animationPlayState: 'running'
  } : {
    animation: 'none'
  };
  
  const viewportClass = `sf-marquee-viewport ${!pauseOnHover ? 'sf-marquee-no-pause' : ''}`;

  return (
    <div className="sf-marquee-wrap">
      <div className="sf-marquee-head">
        <div className="sf-marquee-meta">
          <div className="sf-marquee-title">Featured Products</div>
          <div className="sf-marquee-sub">
            {isEnabled ? `Auto-scroll ${speed}s • ${pauseOnHover ? 'Hover to pause' : 'Always moving'}` : 'Manual scroll'}
          </div>
        </div>
        <button
          className="sf-marquee-settings"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            console.log('[ShopFeed] Opening manage modal...');
            onOpenManage?.();
          }}
          title="Manage Featured Products"
          aria-label="Manage Featured Products"
        >
          ⭐ Manage
        </button>
      </div>

      <div className={viewportClass}>
        <div className="sf-marquee-track" style={trackStyle}>
          {display.map((pr, idx) => {
            const pid = getRealProductId(pr);
            const img = pr?._image || normalizeProductImage(pr);
            const title = pr?.title || "Product";

            return (
              <div
                key={`${String(pid || "p")}-${idx}`}
                className="sf-marquee-card"
                role="button"
                tabIndex={0}
                onClick={() => onOpenDetails(pid)}
                onKeyDown={(e) => e.key === "Enter" && onOpenDetails(pid)}
              >
                <div className="sf-marquee-imgwrap">
                  {img ? (
                    <img
                      className="sf-marquee-img"
                      src={img}
                      alt={title}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <div className="sf-marquee-ph" />
                  )}
                </div>

                <div className="sf-marquee-name" title={title}>
                  {title}
                </div>

                <div className="sf-marquee-actions">
                  <button
                    className="sf-mini sf-mini-ghost"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenDetails(pid);
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* =========================================
   Manage Featured Modal
   ========================================= */
function ManageFeaturedModal({
  open,
  onClose,
  products,
  featuredIds,
  featuredVariantIds,
  marqueeConfig,
  toggleFeatured,
  clearFeatured,
  updateMarqueeConfig,
  saving,
}) {
  const [q, setQ] = useState("");
  
  // New state for dropdowns
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [expandedSection, setExpandedSection] = useState("settings"); // "settings" or "products"

  useEffect(() => {
    if (!open) {
      setQ("");
      setSelectedIndustry("");
      setSelectedCategory("");
      setCategories([]);
      setFilteredProducts([]);
    }
  }, [open]);

  // Define callback functions first, before using them in useEffect
  const loadIndustries = useCallback(async () => {
    try {
      // Get unique industries from shop's own products
      const shopProducts = products || [];
      const industryMap = new Map();
      
      shopProducts.forEach(product => {
        if (product.industryId && product.industryName) {
          industryMap.set(product.industryId, {
            industryId: product.industryId,
            name: product.industryName
          });
        }
      });
      
      const uniqueIndustries = Array.from(industryMap.values())
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setIndustries(uniqueIndustries);
    } catch (e) {
      console.error('Failed to load industries:', e);
    }
  }, [products]);

  const loadCategoriesForIndustry = useCallback(async (industryId) => {
    setLoadingCategories(true);
    setSelectedCategory("");
    try {
      // Get unique categories from shop's products for the selected industry
      const shopProducts = products || [];
      const categoryMap = new Map();
      
      shopProducts
        .filter(product => {
          const match = product.industryId === industryId;
          return match;
        })
        .forEach(product => {
          if (product.categoryId && product.categoryLabel) {
            categoryMap.set(product.categoryId, {
              categoryId: product.categoryId,
              label: product.categoryLabel
            });
          }
        });
      
      const uniqueCategories = Array.from(categoryMap.values())
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setCategories(uniqueCategories);
    } catch (e) {
      console.error('Failed to load categories:', e);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, [products]);

  const loadProductsForCategory = useCallback(async (categoryId) => {
    setLoadingProducts(true);
    try {
      // Filter shop's products by selected category, then expand by colour variant
      const shopProducts = products || [];
      const categoryProducts = shopProducts.filter(product => {
        const match = product.categoryId === categoryId || 
                     product.categoryLabel === categoryId ||
                     product.category === categoryId;
        return match;
      });
      setFilteredProducts(expandProductsForFeaturedModal(categoryProducts));
    } catch (e) {
      console.error('Failed to load products:', e);
      setFilteredProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [products]);

  // Load industries on modal open
  useEffect(() => {
    if (open && industries.length === 0) {
      loadIndustries();
    }
  }, [open, industries.length, loadIndustries]);

  // Load categories when industry changes
  useEffect(() => {
    if (selectedIndustry) {
      loadCategoriesForIndustry(selectedIndustry);
    } else {
      setCategories([]);
      setSelectedCategory("");
      setFilteredProducts([]);
    }
  }, [selectedIndustry, loadCategoriesForIndustry]);

  // Load products when category changes
  useEffect(() => {
    if (selectedCategory) {
      loadProductsForCategory(selectedCategory);
    } else {
      setFilteredProducts([]);
    }
  }, [selectedCategory, loadProductsForCategory]);

  if (!open) return null;

  // Expand to one card per colour variant so shop can see each variant
  const expandedProducts = expandProductsForFeaturedModal(products || []);
  const filtered = expandedProducts.filter((p) =>
    String(p?.title || p?.name || "")
      .toLowerCase()
      .includes(String(q || "").toLowerCase())
  );

  return (
    <div className="sf-modal-backdrop" onClick={onClose}>
      <div className="sf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sf-modal-top">
          <div className="sf-modal-title">⭐ Manage Featured Row</div>
          <button className="sf-btn sf-btn-ghost" type="button" onClick={onClose} disabled={saving}>
            Close
          </button>
        </div>

        {/* Currently Featured Products */}
        <div className="sf-feature-list">
          {filtered.length === 0 ? (
            <div className="sf-empty">No products match your search.</div>
          ) : (
            filtered.map((p) => {
              // _parentId is set on expanded variant cards
              const pid = String(p._parentId || getRealProductId(p) || "");
              const vid = p._variantId ? String(p._variantId) : null;
              const img = p?._image || normalizeProductImage(p);
              // Variant cards: check featuredVariantIds; simple products: check featuredIds
              const isOn = vid
                ? (featuredVariantIds || []).map(String).includes(vid)
                : (featuredIds || []).map(String).includes(pid);

              return (
                <div key={pid} className="sf-feature-item">
                  <label className="sf-feature-left">
                    <input type="checkbox" checked={isOn} disabled={saving} onChange={() => toggleFeatured(vid, pid)} />
                    <div className="sf-feature-thumb">
                      {img ? <img src={img} alt={p.title || p.name || "Product"} /> : <div className="sf-marquee-ph" />}
                    </div>
                    <div className="sf-feature-meta">
                      <div className="sf-feature-title">{p.title || p.name || "Product"}</div>
                      <div className="sf-meta">
                        {(p.currency || "USD") + " " + Number(p.price || p.localPrice || 0).toFixed(2)}
                      </div>
                      {p.categoryLabel && (
                        <div className="sf-meta-category">{p.categoryLabel}</div>
                      )}
                    </div>
                  </label>
                </div>
              );
            })
          )}
        </div>

        {/* Section Menu */}
        <div className="sf-section-menu">
          <button
            className={`sf-section-btn ${expandedSection === "settings" ? "sf-section-btn--active" : ""}`}
            onClick={() => setExpandedSection("settings")}
            type="button"
          >
            📱 Scrolling Settings
          </button>
          <button
            className={`sf-section-btn ${expandedSection === "products" ? "sf-section-btn--active" : ""}`}
            onClick={() => setExpandedSection("products")}
            type="button"
          >
            ➕ Add Products
          </button>
        </div>

        {/* Scrolling Settings Section */}
        {expandedSection === "settings" && (
          <div className="sf-modal-section">
            <div className="sf-controls-row">
              <label className="sf-checkbox-label">
                <input
                  type="checkbox"
                  checked={marqueeConfig?.enabled !== false}
                  onChange={(e) => updateMarqueeConfig({ ...marqueeConfig, enabled: e.target.checked })}
                  disabled={saving}
                />
                Enable auto-scroll
              </label>
            </div>
            
            {marqueeConfig?.enabled !== false && (
              <>
                <div className="sf-controls-row">
                  <label className="sf-speed-label">
                    <div>
                      <span>Speed</span>
                      <span className="sf-speed-hint">
                        {marqueeConfig?.speed <= 10 ? 'Fast' : marqueeConfig?.speed <= 25 ? 'Medium' : 'Slow'}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="5"
                      max="60"
                      value={marqueeConfig?.speed || 26}
                      onChange={(e) => updateMarqueeConfig({ ...marqueeConfig, speed: Number(e.target.value) })}
                      className="sf-speed-slider"
                      disabled={saving}
                    />
                    <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                      {marqueeConfig?.speed || 26}s per cycle
                    </div>
                  </label>
                </div>
                
                <div className="sf-controls-row">
                  <label className="sf-checkbox-label">
                    <input
                      type="checkbox"
                      checked={marqueeConfig?.pauseOnHover !== false}
                      onChange={(e) => updateMarqueeConfig({ ...marqueeConfig, pauseOnHover: e.target.checked })}
                      disabled={saving}
                    />
                    Pause on hover
                  </label>
                </div>
              </>
            )}
          </div>
        )}

        {/* Add Products Section */}
        {expandedSection === "products" && (
          <div className="sf-modal-section">
          <div className="sf-dropdown-row">
            <div className="sf-dropdown-group">
              <label className="sf-dropdown-label">Industry</label>
              <select 
                className="sf-dropdown"
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                disabled={saving || industries.length === 0}
              >
                <option value="">Select Industry</option>
                {industries.map((industry) => (
                  <option key={industry.industryId} value={industry.industryId}>
                    {industry.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="sf-dropdown-group">
              <label className="sf-dropdown-label">Category</label>
              <select 
                className="sf-dropdown"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={saving || !selectedIndustry || loadingCategories || categories.length === 0}
              >
                <option value="">
                  {!selectedIndustry ? "Select Industry First" 
                   : loadingCategories ? "Loading..." 
                   : categories.length === 0 ? "No Categories Available"
                   : "All Categories"}
                </option>
                {categories.map((category) => (
                  <option key={category.categoryId} value={category.categoryId}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Product Grid */}
          {selectedCategory && (
            <div style={{marginTop: "16px"}}>
              {loadingProducts ? (
                <div className="sf-empty">Loading products...</div>
              ) : filteredProducts.length === 0 ? (
                <div className="sf-empty">No products in this category.</div>
              ) : (
                <>
                  <label className="sf-dropdown-label" style={{marginBottom: "12px", display: "block"}}>
                    {filteredProducts.length} Product{filteredProducts.length !== 1 ? "s" : ""} Available
                  </label>
                  <div className="sf-product-grid">
                    {filteredProducts.map((product) => {
                      // _parentId exists on expanded variant cards; fall back to real id
                      const productId = String(product._parentId || getRealProductId(product) || "");
                      const variantId = product._variantId ? String(product._variantId) : null;
                      const img = product?._image || normalizeProductImage(product);
                      // Variant cards: check featuredVariantIds; simple: check featuredIds
                      const isAlreadyFeatured = variantId
                        ? (featuredVariantIds || []).map(String).includes(variantId)
                        : (featuredIds || []).map(String).includes(productId);
                      
                      return (
                        <div key={productId} className="sf-product-card">
                          <div className="sf-product-image">
                            {img ? (
                              <img src={img} alt={product.title || product.name || "Product"} />
                            ) : (
                              <div className="sf-product-placeholder">No Image</div>
                            )}
                            {isAlreadyFeatured && (
                              <div className="sf-product-badge">✓ Featured</div>
                            )}
                          </div>
                          <div className="sf-product-info">
                            <div className="sf-product-title">{product.title || product.name || "Product"}</div>
                            <div className="sf-product-price">
                              {product.currency || "USD"} {Number(product.price || product.localPrice || 0).toFixed(2)}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`sf-product-btn ${isAlreadyFeatured ? "sf-product-btn--disabled" : "sf-product-btn--primary"}`}
                            onClick={() => toggleFeatured(variantId, productId)}
                            disabled={saving || isAlreadyFeatured}
                          >
                            {isAlreadyFeatured ? "✓ Featured" : "+ Add"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        )}

        {/* Modal Actions */}
        <div className="sf-modal-actions">
          <button 
            className="sf-btn sf-btn-primary" 
            type="button" 
            onClick={onClose}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save & Close"}
          </button>
          <button 
            className="sf-btn sf-btn-danger" 
            type="button" 
            onClick={clearFeatured}
            disabled={saving}
          >
            Clear Featured
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================
   MAIN
   ========================================= */
export default function ShopFeed() {
  const { shop, refreshMe } = useShopAuth();
  const shopId = cleanIdForPath(shop?._id || shop?.id);
  const shopName = shop?.shopName || "Your Shop";
  const [composerLogoUrl, setComposerLogoUrl] = useState("");
  const [composerLogoVersion, setComposerLogoVersion] = useState(Date.now());
  const [posts, setPosts] = useState([]);
  const [postsNextBefore, setPostsNextBefore] = useState("");
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [allProducts, setAllProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // ✅ show real debug info on screen
  const [debug, setDebug] = useState("");

  // Profile modal
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [modalLogoUrl, setModalLogoUrl] = useState("");
  const [modalCoverImage, setModalCoverImage] = useState("");
  const [modalBio, setModalBio] = useState("");
  const [modalLogoFile, setModalLogoFile] = useState(null);
  const [modalCoverFile, setModalCoverFile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // composer
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);
  const [busyPost, setBusyPost] = useState(false);

  // featured (from BACKEND)
  const [featureOpen, setFeatureOpen] = useState(false);
  const [featuredIds, setFeaturedIds] = useState([]);         // parent product IDs
  const [featuredVariantIds, setFeaturedVariantIds] = useState([]); // specific variant IDs
  const [marqueeConfig, setMarqueeConfig] = useState({ enabled: true, speed: 26, pauseOnHover: true });
  const [savingFeatured, setSavingFeatured] = useState(false);

  // Debug modal state changes
  useEffect(() => {
    console.log('[ShopFeed] featureOpen changed to:', featureOpen, 'products:', allProducts?.length, 'featured:', featuredIds?.length);
  }, [featureOpen, allProducts?.length, featuredIds?.length]);

  // share dropdown
  const [shareDropdownOpen, setShareDropdownOpen] = useState(null);
  const [postMenuOpen, setPostMenuOpen] = useState(null);

  // Track posts currently being liked to prevent multiple simultaneous requests
  const likingPostsRef = useRef(new Set());

  const nav = useNavigate();

  useEffect(() => {
    const fromContext = shop?.logoUrl || shop?.logo || "";
    if (fromContext) {
      setComposerLogoUrl(fromContext);
      setComposerLogoVersion(Date.now());
    }
  }, [shop?.logoUrl, shop?.logo]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getShopMe();
        const freshShop = data?.shop || data || {};
        if (!active) return;
        const freshLogo = freshShop?.logoUrl || freshShop?.logo || "";
        if (freshLogo) {
          setComposerLogoUrl(freshLogo);
          setComposerLogoVersion(Date.now());
        }
      } catch {
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  function openProfileModal() {
    setModalLogoUrl(shop?.logoUrl || shop?.logo || "");
    setModalCoverImage(shop?.coverImage || "");
    setModalBio(shop?.bio || "");
    setModalLogoFile(null);
    setModalCoverFile(null);
    setProfileModalOpen(true);
  }

  function closeProfileModal() {
    setProfileModalOpen(false);
    setModalLogoFile(null);
    setModalCoverFile(null);
  }

  async function handleSaveProfile() {
    try {
      setSavingProfile(true);
      setErr("");
      setDebug("Updating profile...");

      // Upload logo if changed
      if (modalLogoFile) {
        const formData = new FormData();
        formData.append("shopLogo", modalLogoFile);
        
        console.log("📸 Uploading shop logo...");
        const logoResult = await request("/shop-profile/me/logo", {
          method: "PUT",
          body: formData,
          auth: true,
        });
        console.log("✅ Logo uploaded:", logoResult);

        const freshLogo = logoResult?.logoUrl || logoResult?.shop?.logoUrl || logoResult?.shop?.logo || "";
        if (freshLogo) {
          setComposerLogoUrl(freshLogo);
          setComposerLogoVersion(Date.now());
        }
      }

      // Upload cover if changed
      if (modalCoverFile) {
        const formData = new FormData();
        formData.append("cover", modalCoverFile);
        
        console.log("📸 Uploading shop cover...");
        const coverResult = await request("/shop-profile/me/cover", {
          method: "PUT",
          body: formData,
          auth: true,
        });
        console.log("✅ Cover uploaded:", coverResult);
      }

      // Update bio
      console.log("💬 Updating shop bio...");
      await request("/shop-profile/me", {
        method: "PUT",
        body: { bio: modalBio },
        auth: true,
      });
      console.log("✅ Bio updated");

      setDebug("Profile updated successfully!");
      
      // Refresh shop context
      if (refreshMe) {
        await refreshMe();
      }

      closeProfileModal();
    } catch (e) {
      console.error("❌ Profile update failed:", e);
      setErr(e?.message || "Failed to update profile");
      setDebug(`Profile update failed: ${e?.message || "Unknown error"}`);
    } finally {
      setSavingProfile(false);
    }
  }

  async function loadAll(silent = false) {
    setErr("");
    if (!silent) setLoading(true);
    // don't clear debug if silent, we might want to keep it
    if (!silent) setDebug("");

    try {
      const postsData = await getShopFeed();
      const normalizedPosts = normalizePostsFromResponse(postsData);
      setPosts(normalizedPosts);
      setPostsNextBefore(postsData?.hasMore && postsData?.nextBefore ? String(postsData.nextBefore) : "");

      // Debug: check if posts have Cloudinary URLs
      const postsWithMedia = normalizedPosts.filter(p => p.__mediaUrl);
      if (postsWithMedia.length > 0) {
        console.log(`📸 [Shop Frontend] Loaded ${postsWithMedia.length} posts with media URLs:`);
        postsWithMedia.slice(0, 3).forEach(p => {
          const url = p.__mediaUrl || p.mediaUrl || p.media?.[0]?.url;
          const isCloudinary = url && url.includes('cloudinary');
          console.log(`  ${isCloudinary ? '✅ Cloudinary' : '⚠️  Local'}: ${url}`);
        });
      }

      const prodData = await getMyShopProducts();
      // debug: log backend response shape to help diagnose missing products
      // eslint-disable-next-line no-console
      console.log("ShopFeed: getMyShopProducts response:", prodData);

      // Safer extraction: support direct array, {products}, {items}, {data: {products}}, or legacy shapes
      let productsList = [];
      if (Array.isArray(prodData)) productsList = prodData;
      else if (Array.isArray(prodData?.products)) productsList = prodData.products;
      else if (Array.isArray(prodData?.items)) productsList = prodData.items;
      else if (Array.isArray(prodData?.data?.products)) productsList = prodData.data.products;
      else if (Array.isArray(prodData?.data)) productsList = prodData.data;
      else productsList = [];
      const prods = Array.isArray(productsList) ? productsList : [];

      setAllProducts(
        prods.map((p) => ({
          ...p,
          _image: normalizeProductImage(p),
        }))
      );

      const loadResult = await loadFeaturedIdsFromBackendSafe(setDebug);
      setFeaturedIds(Array.isArray(loadResult.featuredIds) ? loadResult.featuredIds.map(String) : []);
      setFeaturedVariantIds(Array.isArray(loadResult.featuredVariantIds) ? loadResult.featuredVariantIds.map(String) : []);
      if (loadResult.marqueeConfig) {
        console.log('[ShopFeed] Loaded marquee config:', loadResult.marqueeConfig);
        setMarqueeConfig(loadResult.marqueeConfig);
      }
    } catch (e) {
      const errMsg = e?.message || "Failed to load shop feed";
      setErr(errMsg);
      
      // Better debug info for 404s
      if (e?.status === 404) {
        setDebug(`404 Error: Backend route not found. Path tried: ${e?.path || 'unknown'}. Backend may still be deploying...`);
      } else {
        setDebug(`Load failed: (${e?.status || "?"}) ${errMsg}`);
      }
      
      console.error("ShopFeed load error:", e);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadMorePosts() {
    if (!postsNextBefore || loadingMorePosts) return;
    setLoadingMorePosts(true);
    try {
      const data = await getShopFeed({ before: postsNextBefore });
      const more = normalizePostsFromResponse(data);
      setPosts((prev) => {
        const seen = new Set(prev.map((p) => cleanIdForPath(p.__id || p._id || p.id)));
        return [...prev, ...more.filter((p) => !seen.has(cleanIdForPath(p.__id || p._id || p.id)))];
      });
      setPostsNextBefore(data?.hasMore && data?.nextBefore ? String(data.nextBefore) : "");
    } catch (e) {
      setErr(e?.message || "Failed to load more posts");
    } finally {
      setLoadingMorePosts(false);
    }
  }

  const featuredProducts = useMemo(() => {
    const set = new Set((featuredIds || []).map(String));
    return (allProducts || []).filter((p) => set.has(String(getRealProductId(p))));
  }, [allProducts, featuredIds]);

  // Expand featured parent products into one card per SELECTED variant.
  // If no variantIds are tracked (legacy), fall back to showing each parent once.
  const productsForRow = useMemo(() => {
    if (!featuredProducts.length) {
      const hasFeaturedSelection =
        (Array.isArray(featuredIds) && featuredIds.length > 0) ||
        (Array.isArray(featuredVariantIds) && featuredVariantIds.length > 0);

      // Legacy / clean state only: no featured selections saved yet.
      if (!hasFeaturedSelection) {
        return expandProductsForFeaturedModal(allProducts || []).slice(0, 10);
      }

      // If only variant selections exist (or product IDs are stale), derive cards by variant IDs.
      const vidSet = new Set((featuredVariantIds || []).map(String).filter(Boolean));
      if (!vidSet.size) return [];

      const variantMatchedParents = (allProducts || []).filter((p) =>
        Array.isArray(p?.variants) && p.variants.some((v) => vidSet.has(String(v?._id || "")))
      );

      const derived = [];
      for (const prod of variantMatchedParents) {
        const variants = Array.isArray(prod.variants)
          ? prod.variants.filter((v) => v && v.isActive !== false)
          : [];

        const optionNames = Array.isArray(prod.options)
          ? prod.options.map((o) => String(o.name || "")).filter(Boolean)
          : [];
        const colorOptionName = optionNames.find((n) => /colou?r/i.test(n)) || optionNames[0] || null;

        for (const v of variants) {
          const vid = String(v._id || "");
          if (!vidSet.has(vid)) continue;

          const variantImg = Array.isArray(v.images) && v.images[0]
            ? makeAbsolute(String(v.images[0]))
            : normalizeProductImage(prod);

          const colorVal = colorOptionName
            ? ((v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || "")
            : "";

          derived.push({
            ...prod,
            _variantId: vid,
            _image: variantImg,
            title: colorVal ? `${prod.title || prod.name || "Product"} – ${colorVal}` : (prod.title || prod.name || "Product"),
          });
        }
      }

      return derived;
    }

    const vidSet = new Set((featuredVariantIds || []).map(String));
    const expanded = [];

    for (const prod of featuredProducts) {
      const variants = Array.isArray(prod.variants)
        ? prod.variants.filter((v) => v && v.isActive !== false)
        : [];

      if (!variants.length) {
        // Simple product — show as-is with the best image
        expanded.push({ ...prod, _image: normalizeProductImage(prod) });
        continue;
      }

      // Detect colour option name
      const optionNames = Array.isArray(prod.options)
        ? prod.options.map((o) => String(o.name || '')).filter(Boolean)
        : [];
      const colorOptionName = optionNames.find((n) => /colou?r/i.test(n)) || optionNames[0] || null;

      if (vidSet.size > 0) {
        // Show ONLY selected variants
        for (const v of variants) {
          const vid = String(v._id || '');
          if (!vidSet.has(vid)) continue;
          const variantImg = Array.isArray(v.images) && v.images[0]
            ? makeAbsolute(String(v.images[0]))
            : normalizeProductImage(prod);
          const colorVal = colorOptionName
            ? ((v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || '')
            : '';
          expanded.push({
            ...prod,
            _image: variantImg,
            title: colorVal ? `${prod.title || prod.name || 'Product'} – ${colorVal}` : (prod.title || prod.name || 'Product'),
          });
        }
      } else {
        // Legacy: no variantIds stored — show one card per colour (first variant per colour)
        const seen = new Set();
        for (const v of variants) {
          const colorVal = colorOptionName
            ? ((v.optionValues || []).find((ov) => ov.name === colorOptionName)?.value || '').toLowerCase()
            : String(v._id || '');
          if (seen.has(colorVal)) continue;
          seen.add(colorVal);
          const variantImg = Array.isArray(v.images) && v.images[0]
            ? makeAbsolute(String(v.images[0]))
            : normalizeProductImage(prod);
          expanded.push({ ...prod, _image: variantImg });
        }
      }
    }

    return expanded.length ? expanded : featuredProducts.map((p) => ({ ...p, _image: normalizeProductImage(p) }));
  }, [allProducts, featuredProducts, featuredIds, featuredVariantIds]);

  function openShopProductDetails(productId) {
    const pid = cleanIdForPath(productId);
    if (!pid) return;

    // ✅ shop app route
    nav(`/product/${encodeURIComponent(pid)}`);
  }

  function openUserBuyPage(productId) {
    const pid = cleanIdForPath(productId);
    if (!pid) return;
    window.open(userBuyUrl(pid), "_blank", "noopener,noreferrer");
  }

  // goToShopMall function removed - not currently used

  // toggleFeatured(variantId, parentId)
  //   variantId  — the variant subdoc _id string (null for simple/non-variant products)
  //   parentId   — the product._id string (always)
  async function toggleFeatured(variantId, parentId) {
    const pid = cleanIdForPath(parentId || variantId);
    const vid = variantId && variantId !== parentId ? String(variantId) : null;
    if (!pid) return;

    setErr("");
    setSavingFeatured(true);

    let nextFeaturedIds, nextVariantIds;

    if (vid) {
      // --- VARIANT CARD: toggle specific variant ---
      const vidSet = new Set((featuredVariantIds || []).map(String));
      if (vidSet.has(vid)) vidSet.delete(vid);
      else vidSet.add(vid);
      nextVariantIds = Array.from(vidSet);

      // Keep parent in featuredIds only if ≥1 variant of this product is selected
      const prod = (allProducts || []).find(p => String(getRealProductId(p)) === String(pid));
      const productAllVariantIds = (prod?.variants || []).map(v => String(v._id));
      const anyStillSelected = productAllVariantIds.some(v => vidSet.has(v));
      const pidSet = new Set((featuredIds || []).map(String));
      if (anyStillSelected) pidSet.add(String(pid));
      else pidSet.delete(String(pid));
      nextFeaturedIds = Array.from(pidSet);
    } else {
      // --- SIMPLE PRODUCT (no variants): toggle parent ---
      const pidSet = new Set((featuredIds || []).map(String));
      if (pidSet.has(pid)) pidSet.delete(pid);
      else pidSet.add(pid);
      nextFeaturedIds = Array.from(pidSet);
      nextVariantIds = (featuredVariantIds || []).map(String);
    }

    setFeaturedIds(nextFeaturedIds);
    setFeaturedVariantIds(nextVariantIds);

    try {
      const result = await saveFeaturedIdsToBackendSafe(nextFeaturedIds, nextVariantIds, marqueeConfig, setDebug);
      setFeaturedIds(Array.isArray(result.featuredIds) ? result.featuredIds.map(String) : nextFeaturedIds);
      setFeaturedVariantIds(Array.isArray(result.featuredVariantIds) ? result.featuredVariantIds.map(String) : nextVariantIds);
      if (result.marqueeConfig) setMarqueeConfig(result.marqueeConfig);
    } catch (e) {
      setErr(e?.message || "Failed to save featured products.");
      const backResult = await loadFeaturedIdsFromBackendSafe(setDebug);
      setFeaturedIds(Array.isArray(backResult.featuredIds) ? backResult.featuredIds.map(String) : []);
      setFeaturedVariantIds(Array.isArray(backResult.featuredVariantIds) ? backResult.featuredVariantIds.map(String) : []);
      if (backResult.marqueeConfig) setMarqueeConfig(backResult.marqueeConfig);
    } finally {
      setSavingFeatured(false);
    }
  }

  async function clearFeatured() {
    setErr("");
    setSavingFeatured(true);

    setFeaturedIds([]);
    setFeaturedVariantIds([]);

    try {
      const result = await saveFeaturedIdsToBackendSafe([], [], marqueeConfig, setDebug);
      setFeaturedIds(Array.isArray(result.featuredIds) ? result.featuredIds.map(String) : []);
      setFeaturedVariantIds(Array.isArray(result.featuredVariantIds) ? result.featuredVariantIds.map(String) : []);
      if (result.marqueeConfig) setMarqueeConfig(result.marqueeConfig);
    } catch (e) {
      setErr(e?.message || "Failed to clear featured products.");
      const backResult = await loadFeaturedIdsFromBackendSafe(setDebug);
      setFeaturedIds(Array.isArray(backResult.featuredIds) ? backResult.featuredIds.map(String) : []);
      setFeaturedVariantIds(Array.isArray(backResult.featuredVariantIds) ? backResult.featuredVariantIds.map(String) : []);
      if (backResult.marqueeConfig) setMarqueeConfig(backResult.marqueeConfig);
    } finally {
      setSavingFeatured(false);
    }
  }

  function updateMarqueeConfig(newConfig) {
    const updated = { ...marqueeConfig, ...newConfig };
    console.log('[ShopFeed] updateMarqueeConfig:', newConfig, '-> updated:', updated);
    setMarqueeConfig(updated);
    
    // Save immediately when config changes
    setSavingFeatured(true);
    saveFeaturedIdsToBackendSafe(featuredIds, featuredVariantIds, updated, setDebug)
      .then((result) => {
        console.log('[ShopFeed] Save result:', result);
        if (result.marqueeConfig) setMarqueeConfig(result.marqueeConfig);
      })
      .catch((e) => {
        console.error('[ShopFeed] Save error:', e);
        setErr(e?.message || "Failed to save marquee settings.");
      })
      .finally(() => {
        setSavingFeatured(false);
      });
  }

  async function onCreatePost(e) {
    if (e?.preventDefault) e.preventDefault();

    const t = String(text || "").trim();
    if (!t && !file) {
      setErr("Write something or choose a photo/video first.");
      return;
    }

    setBusyPost(true);
    setErr("");

    try {
      // ✅ IMPORTANT FIX:
      // If you have a file -> use FormData so backend receives the upload.
      let result;
      if (file) {
        const fd = new FormData();
        if (t) fd.append("text", t);

        // One field only: the route takes media/file/image, and sending the
        // same file twice uploaded it to Cloudinary twice.
        fd.append("media", file);

        result = await createShopPost(fd);
        console.log("📸 [Shop Frontend] Post created with media:", result);
      } else {
        result = await createShopPost({ text: t });
        console.log("📝 [Shop Frontend] Post created (text only):", result);
      }

      // Debug: verify Cloudinary URL is returned
      if (result?.post?.mediaUrl || result?.post?.media) {
        console.log("✅ [Shop Frontend] Media URL saved:", 
          result?.post?.mediaUrl || result?.post?.media?.[0]?.url || result?.post?.media);
      }

      setText("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await loadAll();
    } catch (ex) {
      console.error("❌ Create post failed:", ex);
      setErr(ex?.message || ex?.response?.data?.message || "Create post failed");
      setDebug(`Create post failed: (${ex?.status || "?"}) ${ex?.message || ""}`);
    } finally {
      setBusyPost(false);
    }
  }

  async function onDeletePost(realPostId) {
    const pid = cleanIdForPath(realPostId);
    if (!pid) {
      setErr("Delete failed: invalid post id");
      return;
    }

    setErr("");

    setPosts((prev) =>
      (prev || []).filter((x) => cleanIdForPath(x.__id || x._id || x.id) !== pid)
    );

    const correctPaths = [
      `/api/shop/posts/${encodeURIComponent(pid)}`,
      `/api/shops/posts/${encodeURIComponent(pid)}`,
    ];

    let lastErr = null;
    for (const p of correctPaths) {
      try {
        await request(p, { method: "DELETE" });
        await loadAll();
        return;
      } catch (e) {
        lastErr = e;
      }
    }

    try {
      await deleteShopPost(pid);
      await loadAll();
      return;
    } catch (e2) {
      lastErr = e2;
    }

    await loadAll();

    const errMsg = lastErr?.message || "";
    const msg =
      lastErr?.status === 404 && errMsg.toLowerCase().includes("post not found")
        ? "Delete failed: Post not found in database. It may have already been deleted."
        : lastErr?.status === 404
        ? "Delete failed: backend route not found (404). Backend may still be deploying..."
        : lastErr?.status === 401
        ? "Delete failed: unauthorized (401). Check shopAuth token in request()."
        : lastErr?.status === 403
        ? "Delete failed: not allowed (403). You may not own this post."
        : errMsg || "Delete failed. Backend rejected delete.";

    setErr(msg);
    setDebug(`Delete failed: (${lastErr?.status || "?"}) ${errMsg}`);
  }

  async function onEditPost(realPostId, newText) {
    const pid = cleanIdForPath(realPostId);
    const t = String(newText || "").trim();
    if (!pid) {
      setErr("Edit failed: invalid post id");
      return;
    }

    setErr("");

    // Optimistic update
    setPosts((prev) =>
      (prev || []).map((p) => {
        const id = cleanIdForPath(p.__id || p._id || p.id);
        if (id !== pid) return p;
        return { ...p, text: t, __text: t, edited: true };
      })
    );

    try {
      await editShopPost(pid, t);
      await loadAll(true);
    } catch (e) {
      await loadAll(true);
      setErr(e?.message || "Edit failed");
      setDebug(`Edit failed: (${e?.status || "?"}) ${e?.message || ""}`);
    }
  }

  async function onLike(realPostId) {
    const pid = cleanIdForPath(realPostId);
    if (!pid) return;

    // Prevent multiple simultaneous likes on same post
    if (likingPostsRef.current.has(pid)) return;
    likingPostsRef.current.add(pid);

    setErr("");
    
    // Optimistic update
    setPosts(prev => prev.map(p => {
      const postId = cleanIdForPath(p.__id || p._id || p.id);
      if (postId === pid) {
        const currentLiked = p.likedByMe || false;
        const currentCount = typeof p.likesCount === 'number' ? p.likesCount : (Array.isArray(p.likes) ? p.likes.length : 0);
        return {
          ...p,
          likedByMe: !currentLiked,
          likesCount: currentLiked ? Math.max(0, currentCount - 1) : currentCount + 1
        };
      }
      return p;
    }));
    
    try {
      await toggleShopLike(pid);
      // Don't reload - we already updated optimistically
    } catch (e) {
      // Revert on error
      likingPostsRef.current.delete(pid);
      setPosts(prev => prev.map(p => {
        const postId = cleanIdForPath(p.__id || p._id || p.id);
        if (postId === pid) {
          const currentLiked = p.likedByMe || false;
          const currentCount = p.likesCount || 0;
          return {
            ...p,
            likedByMe: !currentLiked,
            likesCount: currentLiked ? currentCount + 1 : Math.max(0, currentCount - 1)
          };
        }
        return p;
      }));
      setErr(e?.message || "Like failed");
      setDebug(`Like failed: (${e?.status || "?"}) ${e?.message || ""}`);
    } finally {
      // Always clear the liking state
      likingPostsRef.current.delete(pid);
    }
  }

  async function onAddComment(realPostId, commentText) {
    const pid = cleanIdForPath(realPostId);
    const t = String(commentText || "").trim();
    if (!pid || !t) return;

    setErr("");
    
    // Optimistic comment add
    const tempComment = {
      _id: `temp-${Date.now()}`,
      text: t,
      createdAt: new Date().toISOString(),
      author: { shopName: shopName || "Shop" },
      replies: []
    };
    
    setPosts(prev => prev.map(p => {
      const postId = cleanIdForPath(p.__id || p._id || p.id);
      if (postId === pid) {
        const currentComments = Array.isArray(p.comments) ? p.comments : [];
        return {
          ...p,
          comments: [...currentComments, tempComment],
          commentsCount: (p.commentsCount || 0) + 1
        };
      }
      return p;
    }));
    
    try {
      await addShopComment(pid, t);
      // Reload to get real comment with proper ID and data
      await loadAll(true);
    } catch (e) {
      // Revert on error
      setPosts(prev => prev.map(p => {
        const postId = cleanIdForPath(p.__id || p._id || p.id);
        if (postId === pid) {
          return {
            ...p,
            comments: (p.comments || []).filter(c => c._id !== tempComment._id),
            commentsCount: Math.max(0, (p.commentsCount || 0) - 1)
          };
        }
        return p;
      }));
      setErr(e?.message || "Comment failed");
      setDebug(`Comment failed: (${e?.status || "?"}) ${e?.message || ""}`);
    }
  }

  async function onAddReply(realPostId, commentId, replyText) {
    const pid = cleanIdForPath(realPostId);
    const cid = cleanIdForPath(commentId);
    const t = String(replyText || "").trim();
    if (!pid || !cid || !t) return;

    setErr("");
    
    try {
      await addShopReply(pid, cid, t);
      // Reload to get updated comments with replies
      await loadAll(true);
    } catch (e) {
      setErr(e?.message || "Reply failed");
      setDebug(`Reply failed: (${e?.status || "?"}) ${e?.message || ""}`);
    }
  }

  // Share functions
  function getPostPermaLink(post) {
    const postId = cleanIdForPath(post?.__id || post?._id || post?.id || "");
    if (!postId) return window.location.href;
    // A link anyone can open: the shop's public feed in the user app, scrolled to the post.
    const userApp = String(process.env.REACT_APP_USER_APP_URL || "https://moondala.one").replace(/\/+$/, "");
    const sid = cleanIdForPath(post?.authorShopId || post?.shopId || shopId || "");
    return sid ? `${userApp}/shop/${sid}/feed?postId=${postId}` : `${userApp}/feed`;
  }

  async function toggleShareDropdown(e, postOrId) {
    if (e) e.stopPropagation();
    
    // Resolve post object
    let post = null;
    if (postOrId && typeof postOrId === 'object') {
      post = postOrId;
    } else {
      post = posts.find(p => cleanIdForPath(p.__id || p._id || p.id) === cleanIdForPath(postOrId));
    }
    
    if (!post) return;
    
    const postId = cleanIdForPath(post.__id || post._id || post.id);
    const shopName = post.author?.shopName || post.shopName || shop?.shopName || "Shop";
    const postText = post.__text || post.text || post.caption || post.content || "";
    
    // ✅ Use Native Share API first (like user feed)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Post by ${shopName}`,
          text: postText || "Check out this post",
          url: getPostPermaLink(post),
        });
        return;
      } catch (err) {
        // User cancelled or share failed
        if (err.name !== 'AbortError') {
          console.warn('Share failed:', err);
        }
      }
    }
    
    // Fallback: Show dropdown menu
    setShareDropdownOpen(prev => (prev === postId ? null : postId));
    setPostMenuOpen(null);
  }

  function togglePostMenu(e, postOrId) {
    if (e) e.stopPropagation();
    const postId = cleanIdForPath(postOrId?.__id || postOrId?._id || postOrId?.id || postOrId);
    if (!postId) return;
    setPostMenuOpen(prev => (prev === postId ? null : postId));
    setShareDropdownOpen(null);
  }

  async function copyPostLink(post) {
    try {
      const link = getPostPermaLink(post);
      await navigator.clipboard.writeText(link);
      alert('Link copied to clipboard!');
      setShareDropdownOpen(null);
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Failed to copy link');
    }
  }

  async function copyPostContent(post) {
    try {
      const postText = post.__text || post.text || post.caption || post.content || "";
      const shopName = post.author?.shopName || post.shopName || shop?.shopName || "Shop";
      const shareText = `"${postText}" - ${shopName} on Moondala`;
      await navigator.clipboard.writeText(shareText);
      alert('Content copied to clipboard!');
      setShareDropdownOpen(null);
    } catch (err) {
      console.error('Copy failed:', err);
      alert('Failed to copy content');
    }
  }

  function shareToWhatsApp(post) {
    const postText = post.__text || post.text || post.caption || post.content || "";
    const shopName = post.author?.shopName || post.shopName || shop?.shopName || "Shop";
    const text = `Check out this post by ${shopName}: "${postText}"\n\n${getPostPermaLink(post)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    setShareDropdownOpen(null);
  }

  function shareToTwitter(post) {
    const postText = post.__text || post.text || post.caption || post.content || "";
    const shopName = post.author?.shopName || post.shopName || shop?.shopName || "Shop";
    const text = `Check out this post by ${shopName} on Moondala: "${postText}"`;
    const shareUrl = getPostPermaLink(post);
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    setShareDropdownOpen(null);
  }

  function shareViaEmail(post) {
    const postText = post.__text || post.text || post.caption || post.content || "";
    const shopName = post.author?.shopName || post.shopName || shop?.shopName || "Shop";
    const subject = `Check out this post from ${shopName}`;
    const body = `I wanted to share this post with you:\n\n"${postText}"\n\nBy ${shopName} on Moondala\n\nCheck it out: ${getPostPermaLink(post)}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setShareDropdownOpen(null);
  }

  function shareToFacebook(post) {
    const shareUrl = getPostPermaLink(post);
    const postText = post.__text || post.text || post.caption || post.content || "";
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(postText)}`, '_blank');
    setShareDropdownOpen(null);
  }

  function shareToTelegram(post) {
    const postText = post.__text || post.text || post.caption || post.content || "";
    const shopName = post.author?.shopName || post.shopName || shop?.shopName || "Shop";
    const text = `${postText}\n\nBy ${shopName} on Moondala`;
    const shareUrl = getPostPermaLink(post);
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`, '_blank');
    setShareDropdownOpen(null);
  }

  async function shareComment(post, comment) {
    const shopName = post?.author?.shopName || post?.shopName || shop?.shopName || "Shop";
    const commentText = String(comment?.text || "").trim() || "Comment";
    const url = getPostPermaLink(post || {});

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Comment from ${shopName}`,
          text: commentText,
          url,
        });
        return;
      } catch (err) {
        if (err.name !== "AbortError") {
          console.warn("Comment share failed:", err);
        }
      }
    }

    try {
      await navigator.clipboard.writeText(`${commentText}\n\n${url}`);
      alert("Comment copied to clipboard!");
    } catch (err) {
      console.error("Copy failed:", err);
      alert("Failed to copy comment");
    }
  }

  function shareToLinkedIn(post) {
    const shareUrl = getPostPermaLink(post);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
    setShareDropdownOpen(null);
  }

  async function onCommentLike(realPostId, commentId) {
    const pid = cleanIdForPath(realPostId);
    const cid = cleanIdForPath(commentId);
    if (!pid || !cid) return;

    setErr("");
    
    // Optimistic update
    setPosts(prev => prev.map(p => {
      const postId = cleanIdForPath(p.__id || p._id || p.id);
      if (postId === pid) {
        const updatedComments = (p.comments || []).map(c => {
          if (cleanIdForPath(c._id || c.id) === cid) {
            const liked = c.likedByMe || false;
            const count = c.likesCount || 0;
            return {
              ...c,
              likedByMe: !liked,
              likesCount: liked ? Math.max(0, count - 1) : count + 1
            };
          }
          return c;
        });
        return { ...p, comments: updatedComments };
      }
      return p;
    }));

    try {
      await toggleCommentLike(pid, cid);
    } catch (e) {
      // Revert
      setPosts(prev => prev.map(p => {
        const postId = cleanIdForPath(p.__id || p._id || p.id);
        if (postId === pid) {
          const updatedComments = (p.comments || []).map(c => {
            if (cleanIdForPath(c._id || c.id) === cid) {
              const liked = c.likedByMe || false;
              const count = c.likesCount || 0;
              return {
                ...c,
                likedByMe: !liked,
                likesCount: liked ? count + 1 : Math.max(0, count - 1)
              };
            }
            return c;
          });
          return { ...p, comments: updatedComments };
        }
        return p;
      }));
      setErr(e?.message || "Comment like failed");
    }
  }

  async function onCommentEdit(realPostId, commentId, newText) {
    const pid = cleanIdForPath(realPostId);
    const cid = cleanIdForPath(commentId);
    const t = String(newText || "").trim();
    if (!pid || !cid || !t) return;

    setErr("");

    // Optimistic update
    setPosts(prev => prev.map(p => {
      const postId = cleanIdForPath(p.__id || p._id || p.id);
      if (postId === pid) {
        const updatedComments = (p.comments || []).map(c => {
          if (cleanIdForPath(c._id || c.id) === cid) {
            return { ...c, text: t, edited: true };
          }
          return c;
        });
        return { ...p, comments: updatedComments };
      }
      return p;
    }));

    try {
      await editComment(pid, cid, t);
    } catch (e) {
      // Reload on error to get original state
      await loadAll();
      setErr(e?.message || "Edit failed");
    }
  }

  async function onCommentDelete(realPostId, commentId) {
    const pid = cleanIdForPath(realPostId);
    const cid = cleanIdForPath(commentId);
    if (!pid || !cid) return;

    setErr("");

    // Optimistic delete
    setPosts(prev => prev.map(p => {
      const postId = cleanIdForPath(p.__id || p._id || p.id);
      if (postId === pid) {
        const updatedComments = (p.comments || []).filter(c => cleanIdForPath(c._id || c.id) !== cid);
        return {
          ...p,
          comments: updatedComments,
          commentsCount: Math.max(0, (p.commentsCount || 0) - 1)
        };
      }
      return p;
    }));

    try {
      await deleteComment(pid, cid);
    } catch (e) {
      // Reload on error
      await loadAll();
      setErr(e?.message || "Delete failed");
    }
  }

  return (
    <div className="sf-wrap">
      <div className="sf-wrap-inner">
        {/* Profile Section */}
        <div className="sf-profile-card">
          <div className="sf-cover-area">
            {shop?.coverImage ? (
              <img src={toAbsUrl(shop.coverImage)} alt="Cover" className="sf-cover-image" />
            ) : (
              <div className="sf-cover-empty"></div>
            )}
          </div>
          
          <div className="sf-profile-info">
            <div className="sf-avatar">
              {shop?.logoUrl || shop?.logo ? (
                <img src={toAbsUrl(shop.logoUrl || shop.logo)} alt={shopName} className="sf-avatar-image" />
              ) : (
                <div className="sf-avatar-fallback">
                  {shopName.charAt(0).toUpperCase() || "S"}
                </div>
              )}
            </div>
            
            <div className="sf-profile-details">
              <h1 className="sf-profile-name">{shopName}</h1>
              {shop?.bio && <p className="sf-profile-bio">{shop.bio}</p>}
            </div>
            
            <button className="sf-btn sf-btn-primary sf-edit-profile-btn" onClick={openProfileModal}>
              ✏️ Edit Profile
            </button>
          </div>
        </div>

        {/* Profile Edit Modal */}
        {profileModalOpen && (
          <div className="sf-modal-overlay" onClick={closeProfileModal}>
            <div className="sf-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="sf-modal-header">
                <h2 className="sf-modal-title">Edit Profile</h2>
                <button className="sf-modal-close" onClick={closeProfileModal}>✕</button>
              </div>
              
              <div className="sf-modal-body">
                {/* Cover Image */}
                <div className="sf-modal-field">
                  <label className="sf-modal-label">Cover Image</label>
                  <div className="sf-modal-image-preview">
                    {modalCoverFile ? (
                      <img src={URL.createObjectURL(modalCoverFile)} alt="Cover preview" className="sf-modal-preview-img" />
                    ) : modalCoverImage ? (
                      <img src={toAbsUrl(modalCoverImage)} alt="Cover" className="sf-modal-preview-img" />
                    ) : (
                      <div className="sf-modal-preview-empty">No cover image</div>
                    )}
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setModalCoverFile(e.target.files?.[0] || null)}
                    style={{ display: 'none' }}
                  />
                  <button className="sf-btn sf-btn-secondary" onClick={() => coverInputRef.current?.click()}>
                    📷 Choose Cover Image
                  </button>
                </div>

                {/* Avatar/Logo */}
                <div className="sf-modal-field">
                  <label className="sf-modal-label">Shop Logo</label>
                  <div className="sf-modal-avatar-preview">
                    {modalLogoFile ? (
                      <img src={URL.createObjectURL(modalLogoFile)} alt="Logo preview" className="sf-modal-avatar-img" />
                    ) : modalLogoUrl ? (
                      <img src={toAbsUrl(modalLogoUrl)} alt="Logo" className="sf-modal-avatar-img" />
                    ) : (
                      <div className="sf-modal-avatar-fallback">
                        {shopName.charAt(0).toUpperCase() || 'S'}
                      </div>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => setModalLogoFile(e.target.files?.[0] || null)}
                    style={{ display: 'none' }}
                  />
                  <button className="sf-btn sf-btn-secondary" onClick={() => logoInputRef.current?.click()}>
                    📷 Choose Logo
                  </button>
                </div>

                {/* Bio */}
                <div className="sf-modal-field">
                  <label className="sf-modal-label">Bio</label>
                  <textarea
                    className="sf-modal-textarea"
                    value={modalBio}
                    onChange={(e) => setModalBio(e.target.value)}
                    placeholder="Tell customers about your shop..."
                    maxLength={200}
                    rows={4}
                  />
                  <div className="sf-modal-char-count">{modalBio.length}/200</div>
                </div>
              </div>
              
              <div className="sf-modal-footer">
                <button 
                  className="sf-btn sf-btn-ghost" 
                  onClick={closeProfileModal}
                  disabled={savingProfile}
                >
                  Cancel
                </button>
                <button 
                  className="sf-btn sf-btn-primary" 
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                >
                  {savingProfile ? '⏳ Saving...' : '💾 Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="sf-top">
          <div className="sf-sub">
            Welcome, <b>{shopName}</b>
          </div>
        </div>

        {err ? <div className="sf-alert">{err}</div> : null}

        {debug ? (
          <div className="sf-alert" style={{ background: "#0b2440", border: "1px solid #1f3b5a" }}>
            <b>Debug:</b> {debug}
          </div>
        ) : null}

        {/* PRODUCTS ROW */}
        {loading ? (
          <div className="sf-loading">Loading products…</div>
        ) : productsForRow.length ? (
          <ProductsRowMarquee
            products={productsForRow}
            onOpenDetails={openShopProductDetails}
            onOpenUser={openUserBuyPage}
            onOpenManage={() => {
              console.log('[ShopFeed] onOpenManage called, setting featureOpen to true');
              setFeatureOpen(true);
            }}
            marqueeConfig={marqueeConfig}
          />
        ) : (
          <div className="sf-empty" style={{ marginBottom: 16 }}>
            No products yet. Add products to show them on top ✅
            <br />
            <button 
              className="sf-btn sf-btn-primary" 
              type="button"
              onClick={() => {
                console.log('[ShopFeed] Manual manage button clicked');
                setFeatureOpen(true);
              }}
              style={{ marginTop: 8 }}
            >
              ⭐ Manage Featured Products
            </button>
          </div>
        )}

        {/* ADD STORY */}
        <ShopAddStory />

        {/* CREATE POST */}
        <div className="sfcp-wrap">
          <div className="sfcp-row">
            <div className="sfcp-avatar" title={shopName}>
              {composerLogoUrl ? (
                <img
                  src={withCacheBust(toAbsUrl(composerLogoUrl), composerLogoVersion)}
                  alt={shopName}
                  className="sfcp-avatarImg"
                />
              ) : (
                <span className="sfcp-avatarFallback">{shopName.charAt(0).toUpperCase() || "S"}</span>
              )}
            </div>

            <textarea
              className="sfcp-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Share an update with your customers..."
              maxLength={500}
            />
          </div>

          {file ? (
            <div className="sfcp-fileChip" title={file.name}>
              📎 {file.name.length > 40 ? `${file.name.slice(0, 37)}...` : file.name}
              <button
                type="button"
                className="sfcp-fileChipX"
                onClick={() => {
                  setFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
              >
                <X size={14} />
              </button>
            </div>
          ) : null}

          <div className="sfcp-actions">
            <input
              ref={fileRef}
              className="sf-file"
              type="file"
              accept="image/*,video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={{ display: "none" }}
            />

            <button
              className="sfcp-photoBtn"
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              title="Choose photo/video"
            >
              <ImageIcon size={18} />
            </button>

            <button
              className="sfcp-postBtn"
              type="button"
              onClick={onCreatePost}
              disabled={busyPost || (!String(text || "").trim() && !file)}
            >
              {busyPost ? <Loader2 className="sfcp-spin" size={16} /> : "Post"}
            </button>
          </div>
        </div>

        {/* POSTS */}
        {loading && posts.length === 0 ? (
          <div className="sf-loading">Loading posts…</div>
        ) : posts.length === 0 ? (
          <div className="sf-empty">No posts yet — share your first post above!</div>
        ) : (
          <div className="sf-list">
            {posts.map((p) => {
              const realId = cleanIdForPath(p.__id || p._id || p.id || "");
              return (
                <PostCard
                  key={`post-${realId || Math.random()}`}
                  post={p}
                  realId={realId}
                  onDelete={() => onDeletePost(realId)}
                  onEdit={(t) => onEditPost(realId, t)}
                  onLike={() => onLike(realId)}
                  onAddComment={(t) => onAddComment(realId, t)}
                  onAddReply={(commentId, t) => onAddReply(realId, commentId, t)}
                  onCommentLike={(cid) => onCommentLike(realId, cid)}
                  onCommentEdit={(cid, newText) => onCommentEdit(realId, cid, newText)}
                  onCommentDelete={(cid) => onCommentDelete(realId, cid)}
                  onCommentShare={(comment) => shareComment(p, comment)}
                  myShopName={shopName}
                  myShopId={shopId}
                  shareDropdownOpen={shareDropdownOpen}
                  toggleShareDropdown={toggleShareDropdown}
                  closeShareDropdown={() => setShareDropdownOpen(null)}
                  postMenuOpen={postMenuOpen}
                  togglePostMenu={togglePostMenu}
                  closePostMenu={() => setPostMenuOpen(null)}
                  copyPostLink={copyPostLink}
                  copyPostContent={copyPostContent}
                  shareToTwitter={shareToTwitter}
                  shareViaEmail={shareViaEmail}
                  shareToWhatsApp={shareToWhatsApp}
                  shareToFacebook={shareToFacebook}
                  shareToTelegram={shareToTelegram}
                  shareToLinkedIn={shareToLinkedIn}
                />
              );
            })}
            {postsNextBefore ? (
              <div style={{ textAlign: "center", margin: "12px 0" }}>
                <button className="sf-btn sf-btn-ghost" type="button" onClick={loadMorePosts} disabled={loadingMorePosts}>
                  {loadingMorePosts ? "Loading…" : "Load more"}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* FEATURED MODAL */}
        <ManageFeaturedModal
          open={featureOpen}
          onClose={() => {
            console.log('[ShopFeed] Closing manage modal...');
            setFeatureOpen(false);
          }}
          products={allProducts}
          featuredIds={featuredIds}
          featuredVariantIds={featuredVariantIds}
          marqueeConfig={marqueeConfig}
          toggleFeatured={toggleFeatured}
          clearFeatured={clearFeatured}
          updateMarqueeConfig={updateMarqueeConfig}
          saving={savingFeatured}
        />
      </div>
    </div>
  );
}

/* =========================
   Post Card + Comments
   ========================= */
function PostCard({ post, realId, onDelete, onEdit, onLike, onAddComment, onAddReply, onCommentLike, onCommentEdit, onCommentDelete, onCommentShare, myShopName, myShopId, shareDropdownOpen, toggleShareDropdown, closeShareDropdown, postMenuOpen, togglePostMenu, closePostMenu, copyPostLink, copyPostContent, shareToTwitter, shareViaEmail, shareToWhatsApp, shareToFacebook, shareToTelegram, shareToLinkedIn }) {
  const [commentText, setCommentText] = useState("");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [postEditMode, setPostEditMode] = useState(false);
  const [postEditText, setPostEditText] = useState("");
  const cardRef = useRef(null);

  const text = String(post.__text || post.text || post.caption || post.content || "").trim();
  const media = String(post.__mediaUrl || normalizePostMediaUrl(post) || "").trim();

  const likes =
    typeof post.likesCount === "number"
      ? post.likesCount
      : Array.isArray(post.likes)
      ? post.likes.length
      : 0;

  const commentsCount =
    typeof post.commentsCount === "number"
      ? post.commentsCount
      : Array.isArray(post.comments)
      ? post.comments.length
      : 0;

  useEffect(() => {
    const hasOpenState = commentsOpen || postMenuOpen === realId || shareDropdownOpen === realId;
    if (!hasOpenState) return;

    function handlePointerDown(event) {
      if (!cardRef.current) return;
      if (!cardRef.current.contains(event.target)) {
        setCommentsOpen(false);
        if (postMenuOpen === realId) closePostMenu();
        if (shareDropdownOpen === realId) closeShareDropdown();
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [commentsOpen, postMenuOpen, shareDropdownOpen, realId, closePostMenu, closeShareDropdown]);

  return (
    <div className="sf-card sf-post" ref={cardRef}>
      <div className="sf-post-head">
        <div>
          <div className="sf-name">{post.author?.shopName || post.shopName || myShopName || "Shop"}</div>
        </div>

        <div style={{ position: "relative", display: "flex", gap: 10, alignItems: "center" }}>
          <button
            className="sf-btn sf-btn-ghost sf-btn-icon"
            type="button"
            onClick={(e) => togglePostMenu(e, post)}
            aria-label="Post options"
            title="Post options"
          >
            <MoreVertical className="sf-icon" />
          </button>

          {postMenuOpen === realId && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "8px",
                backgroundColor: "#1a1a1a",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                padding: "8px",
                minWidth: "180px",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              <button
                onClick={(e) => {
                  toggleShareDropdown(e, post);
                  closePostMenu();
                }}
                style={{
                  padding: "10px 12px",
                  backgroundColor: "transparent",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  borderRadius: "8px",
                  textAlign: "left",
                  fontSize: "14px",
                  transition: "background-color 0.2s",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "rgba(255,255,255,0.1)")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
              >
                <Share2 className="sf-icon" /> Share
              </button>
              <button
                onClick={() => {
                  setPostEditText(text);
                  setPostEditMode(true);
                  closePostMenu();
                }}
                style={{
                  padding: "10px 12px",
                  backgroundColor: "transparent",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  borderRadius: "8px",
                  textAlign: "left",
                  fontSize: "14px",
                  transition: "background-color 0.2s",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "rgba(255,255,255,0.1)")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
              >
                <Pencil className="sf-icon" /> Edit
              </button>
              <button
                onClick={() => {
                  closePostMenu();
                  onDelete();
                }}
                style={{
                  padding: "10px 12px",
                  backgroundColor: "transparent",
                  border: "none",
                  color: "white",
                  cursor: "pointer",
                  borderRadius: "8px",
                  textAlign: "left",
                  fontSize: "14px",
                  transition: "background-color 0.2s",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "rgba(239,68,68,0.18)")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
              >
                <Trash2 className="sf-icon" /> Delete
              </button>
            </div>
          )}

          {shareDropdownOpen === realId && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "8px",
                backgroundColor: "#1a1a1a",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                padding: "8px",
                minWidth: "200px",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              <button onClick={() => copyPostLink(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                📎 Copy Link
              </button>
              <button onClick={() => copyPostContent(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                📋 Copy Content
              </button>
              
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', margin: '6px 0' }}></div>
              
              <button onClick={() => shareToWhatsApp(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(37, 211, 102, 0.15)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                💬 WhatsApp
              </button>
              <button onClick={() => shareToTelegram(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 136, 204, 0.15)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                ✈️ Telegram
              </button>
              <button onClick={() => shareToTwitter(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(29, 161, 242, 0.15)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                🐦 Twitter/X
              </button>
              <button onClick={() => shareToFacebook(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(24, 119, 242, 0.15)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                📘 Facebook
              </button>
              <button onClick={() => shareToLinkedIn(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 119, 181, 0.15)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                💼 LinkedIn
              </button>
              <button onClick={() => shareViaEmail(post)} style={{
                padding: '10px 12px',
                backgroundColor: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                transition: 'background-color 0.2s',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }} onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>
                ✉️ Email
              </button>
            </div>
          )}
        </div>
      </div>

      {postEditMode ? (
        <div className="sf-replyrow" style={{ marginTop: 6 }}>
          <input
            className="sf-input"
            value={postEditText}
            onChange={(e) => setPostEditText(e.target.value)}
            placeholder="Edit post…"
          />
          <button
            className="sf-btn sf-btn-primary"
            type="button"
            onClick={() => {
              const t = postEditText.trim();
              onEdit(t);
              setPostEditMode(false);
            }}
          >
            Save
          </button>
          <button
            className="sf-btn sf-btn-ghost"
            type="button"
            onClick={() => {
              setPostEditText(text);
              setPostEditMode(false);
            }}
          >
            Cancel
          </button>
        </div>
      ) : text ? (
        <div className="sf-post-text">{text}</div>
      ) : (
        <div className="sf-post-text" style={{ opacity: 0.6 }}>
          (empty post)
        </div>
      )}

      {media ? (
        <div className="sf-media">
          {isVideoUrl(media) ? (
            <video className="sf-video" controls src={media} />
          ) : (
            <img className="sf-img" alt="post" src={media} />
          )}
        </div>
      ) : null}

      <div className="sf-post-actions">
        <button className="sf-pill" type="button" onClick={onLike} disabled={!realId}>
          {post.likedByMe ? "❤️ Liked" : "🤍 Like"} <span className="sf-pill-num">{likes}</span>
        </button>

        <button
          className="sf-pill sf-pill-muted"
          type="button"
          onClick={() => setCommentsOpen((v) => !v)}
          aria-pressed={commentsOpen}
          title={commentsOpen ? "Hide comments" : "Show comments"}
        >
          💬 Comments <span className="sf-pill-num">{commentsCount}</span>
        </button>

      </div>

      {commentsOpen ? (
        <div className="sf-comments-panel">
          <div className="sf-comment-box">
            <input
              className="sf-input"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Write a comment…"
            />
            <button
              className="sf-btn sf-btn-secondary"
              type="button"
              disabled={!realId}
              onClick={() => {
                const t = commentText.trim();
                if (!t) return;
                onAddComment(t);
                setCommentText("");
              }}
            >
              Send
            </button>
          </div>

          {(post.comments || []).length ? (
            <div className="sf-comments">
              {(() => {
                // Thread like the user app: replies are comments with parentComment.
                const all = post.comments || [];
                const idOf = (c) => String(c._id || c.id || "");
                const ids = new Set(all.map(idOf));
                const top = all.filter((c) => !c.parentComment || !ids.has(String(c.parentComment)));
                const kids = (pid) => all.filter((c) => c.parentComment && String(c.parentComment) === pid);
                const item = (c) => (
                  <CommentItem
                    key={idOf(c) || Math.random()}
                    comment={c}
                    postAuthorId={post.authorShopId || post.shopId || ""}
                    onAddReply={(t) => onAddReply(c._id || c.id, t)}
                    onLike={() => onCommentLike(c._id || c.id)}
                    onEdit={(newText) => onCommentEdit(c._id || c.id, newText)}
                    onDelete={() => onCommentDelete(c._id || c.id)}
                    onShare={() => onCommentShare(c)}
                    myShopName={myShopName}
                    myShopId={myShopId}
                    replyCount={kids(idOf(c)).length + (Array.isArray(c.replies) ? c.replies.length : 0)}
                  />
                );
                return top.map((c) => (
                  <div key={`t-${idOf(c)}`}>
                    {item(c)}
                    {kids(idOf(c)).length ? (
                      <div style={{ marginLeft: 22, borderLeft: "2px solid rgba(139,92,246,.35)", paddingLeft: 10 }}>
                        {kids(idOf(c)).map(item)}
                      </div>
                    ) : null}
                  </div>
                ));
              })()}
            </div>
          ) : (
            <div className="sf-empty-comments">No comments yet.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function CommentItem({ comment, postAuthorId, onAddReply, onLike, onEdit, onDelete, onShare, myShopName, myShopId, replyCount }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(comment.text || "");
  const [commentMenuOpen, setCommentMenuOpen] = useState(false);
  const commentRef = useRef(null);

  const authorId = cleanIdForPath(comment.authorShopId || comment.authorUserId || comment.authorId || "");
  const isMine = (myShopId && authorId === myShopId) || (comment.author?.shopName === myShopName);
  const isPostMine = (myShopId && String(postAuthorId) === String(myShopId));

  const canEdit = isMine;
  const canDelete = isMine || isPostMine;

  const likes = comment.likesCount || 0;

  useEffect(() => {
    if (!commentMenuOpen) return;

    function handlePointerDown(event) {
      if (!commentRef.current) return;
      if (!commentRef.current.contains(event.target)) {
        setCommentMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [commentMenuOpen]);

  return (
    <div className="sf-comment" ref={commentRef}>
      <div className="sf-comment-top">
        <div className="sf-comment-name">{comment.author?.shopName || comment.author?.name || "User"}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
          {comment.edited && <div className="sf-meta">(edited)</div>}
          {(canEdit || canDelete) && !editMode ? (
            <button
              className="sf-btn sf-btn-ghost sf-btn-icon"
              type="button"
              onClick={() => setCommentMenuOpen((v) => !v)}
              aria-label="Comment options"
              title="Comment options"
            >
              <MoreVertical className="sf-icon" />
            </button>
          ) : null}

          {commentMenuOpen && !editMode ? (
            <div
              style={{
                position: "absolute",
                top: "100%",
                right: 0,
                marginTop: "8px",
                backgroundColor: "#1a1a1a",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
                padding: "8px",
                minWidth: "160px",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {onShare && (
                <button
                  onClick={() => {
                    setCommentMenuOpen(false);
                    onShare();
                  }}
                  style={{
                    padding: "10px 12px",
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "8px",
                    textAlign: "left",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "rgba(255,255,255,0.1)")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                >
                  <Share2 className="sf-icon" /> Share
                </button>
              )}
              {canEdit && (
                <button
                  onClick={() => {
                    setEditMode(true);
                    setCommentMenuOpen(false);
                  }}
                  style={{
                    padding: "10px 12px",
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "8px",
                    textAlign: "left",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "rgba(255,255,255,0.1)")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                >
                  <Pencil className="sf-icon" /> Edit
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => {
                    setCommentMenuOpen(false);
                    onDelete();
                  }}
                  style={{
                    padding: "10px 12px",
                    backgroundColor: "transparent",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "8px",
                    textAlign: "left",
                    fontSize: "14px",
                    transition: "background-color 0.2s",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                  onMouseEnter={(e) => (e.target.style.backgroundColor = "rgba(239,68,68,0.18)")}
                  onMouseLeave={(e) => (e.target.style.backgroundColor = "transparent")}
                >
                  <Trash2 className="sf-icon" /> Delete
                </button>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {editMode ? (
        <div className="sf-replyrow">
          <input
            className="sf-input"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            placeholder="Edit comment…"
          />
          <button
            className="sf-btn sf-btn-primary"
            type="button"
            onClick={() => {
              const t = editText.trim();
              if (!t) return;
              onEdit(t);
              setEditMode(false);
            }}
          >
            Save
          </button>
          <button
            className="sf-btn sf-btn-ghost"
            type="button"
            onClick={() => {
              setEditText(comment.text || "");
              setEditMode(false);
            }}
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="sf-comment-text"><MentionText text={comment.text} /></div>
      )}

      <div className="sf-replybar">
        <button className="sf-btn sf-btn-mini" type="button" onClick={onLike}>
          {comment.likedByMe ? "❤️" : "🤍"} {likes > 0 && likes}
        </button>
        <button
          className="sf-btn sf-btn-mini sf-btn-icon"
          type="button"
          onClick={() => setReplyOpen((p) => !p)}
          title={replyOpen ? "Cancel reply" : "Reply"}
          aria-label={replyOpen ? "Cancel reply" : "Reply"}
        >
          {replyOpen ? <X className="sf-icon" /> : <CornerUpLeft className="sf-icon" />}
        </button>
        {!editMode && (
          <div className="sf-meta">Replies: {typeof replyCount === "number" ? replyCount : (comment.replies || []).length}</div>
        )}
      </div>

      {replyOpen ? (
        <div className="sf-replyrow">
          <input
            className="sf-input"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a reply…"
          />
          <button
            className="sf-btn sf-btn-primary"
            type="button"
            onClick={() => {
              const t = replyText.trim();
              if (!t) return;
              onAddReply(t);
              setReplyText("");
              setReplyOpen(false);
            }}
          >
            Send
          </button>
        </div>
      ) : null}

      {(comment.replies || []).map((r) => (
        <div key={r._id || r.id || Math.random()} className="sf-reply">
          <div className="sf-comment-top">
            <div className="sf-comment-name">{r.author?.shopName || r.author?.name || "User"}</div>
          </div>
          <div className="sf-comment-text"><MentionText text={r.text} /></div>
        </div>
      ))}
    </div>
  );
}
